import {useMemo} from 'react';

import {type OpenOrdersQuery, useOpenOrdersQuery} from '@/graphql/types';
import useExcludeModificationCarts from '@/hooks/useExcludeModificationCarts';
import useIdentity from '@/hooks/useIdentity';
import useOpenOrdersFulfillmentDetailFilters from '@/hooks/useOpenOrdersFulfillmentDetailFilters';
import {CATERING_MENU_PATH} from '@/paths';
import {compilePath} from '@/utils';
import {DateTimeFormats, formatEventBarDate} from '@/utils/dateFormat';
import RequiredPath from '@/utils/RequiredPath';
import {formatFulfillmentLocation, isPastEvent} from './helpers/eventBarRecentUtils';

type FulfillmentDetailNode = RequiredPath<
  OpenOrdersQuery,
  ['identity', 'fulfillmentDetails', 'edges', number, 'node']
>;
type DraftConsumerCart = RequiredPath<FulfillmentDetailNode, ['consumerCarts', number]>;

export type EventBarDraftOrder = {
  cartId: string;
  fulfillmentDetailId: string;
  catererId: string;
  catererName: string;
  imageUrl: string | null;
  locationLabel: string;
  dateLabel: string;
  timeLabel: string;
  headcount: number | null;
  resumeHref: string;
};

export const MAX_DRAFT_ORDERS = 3;

const toDraftOrder = (
  fulfillmentDetail: FulfillmentDetailNode,
  cart: DraftConsumerCart,
): EventBarDraftOrder => ({
  cartId: cart.id,
  fulfillmentDetailId: fulfillmentDetail.id,
  catererId: cart.caterer.id,
  catererName: cart.caterer.name,
  imageUrl: cart.caterer.brand?.heroImage?.mobileHeroUrl ?? null,
  locationLabel: formatFulfillmentLocation(fulfillmentDetail.address),
  dateLabel: formatEventBarDate(
    fulfillmentDetail.eventAt,
    fulfillmentDetail.address.timeZoneIdentifier,
  ),
  timeLabel: formatEventBarDate(
    fulfillmentDetail.eventAt,
    fulfillmentDetail.address.timeZoneIdentifier,
    DateTimeFormats.HourWithMinutes,
  ),
  headcount: fulfillmentDetail.headcount ?? null,
  resumeHref: compilePath(
    CATERING_MENU_PATH,
    {catererUrlId: cart.caterer.urlId},
    {cartId: cart.id},
  ),
});

/**
 * Flattens the open-orders query into the draft-order cells shown in the event bar's What panel.
 * Past-dated events are dropped and the newest carts win.
 */
export const toEventBarDraftOrders = (
  data: OpenOrdersQuery | undefined,
  now = Date.now(),
  max = MAX_DRAFT_ORDERS,
): EventBarDraftOrder[] =>
  (data?.identity?.fulfillmentDetails?.edges ?? [])
    .flatMap(edge => {
      const fulfillmentDetail = edge.node;
      if (!fulfillmentDetail || isPastEvent(fulfillmentDetail.eventAt, now)) return [];
      return fulfillmentDetail.consumerCarts.map(cart => ({cart, fulfillmentDetail}));
    })
    .sort((a, b) => new Date(b.cart.updatedAt).getTime() - new Date(a.cart.updatedAt).getTime())
    .slice(0, max)
    .map(({cart, fulfillmentDetail}) => toDraftOrder(fulfillmentDetail, cart));

const useEventBarDraftOrders = (): EventBarDraftOrder[] => {
  const {isLoggedIn} = useIdentity();
  const excludeModificationCarts = useExcludeModificationCarts();
  const fulfillmentDetailFilters = useOpenOrdersFulfillmentDetailFilters();
  const {data} = useOpenOrdersQuery({
    skip: !isLoggedIn,
    variables: {...fulfillmentDetailFilters, excludeModificationCarts},
  });

  return useMemo(() => (isLoggedIn ? toEventBarDraftOrders(data) : []), [isLoggedIn, data]);
};

export default useEventBarDraftOrders;
